import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Container } from "@/components/common/Primitives";
import { api, errorCode } from "@/lib/api";
import { errorText, useLang } from "@/lib/i18n";
import { NAV, Logo, LangSwitch } from "./Header";

/* ------------------------------------------------------------------ newsletter */

const field = "h-12 w-full rounded-full border border-white/15 bg-white/[0.06] px-5 text-[0.9375rem] text-white outline-none transition-[border-color,background-color] placeholder:text-white/40 focus:border-white/50 focus:bg-white/[0.09]";

export const Newsletter = () => {
  const { lang, tx } = useLang();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [state, setState] = useState("idle");
  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setState("loading");
    try {
      const { data } = await api.post("/newsletter", { email, name, lang });
      setState("done");
      toast.success(data.already ? tx("Du er alt på lista.", "You're already on the list.") : tx("Takk! Du er på lista.", "Thank you! You're on the list."));
    } catch (err) {
      setState("idle");
      toast.error(errorText(errorCode(err), lang));
    }
  };
  return (
    <div data-testid="newsletter-section">
      <p className="text-sm font-semibold text-white">{tx("Nytt frå hotellet", "News from the hotel")}</p>
      <p className="mt-2 max-w-sm text-[0.9375rem] leading-relaxed text-white/60">
        {tx("Få menyar og beskjed om arrangement på e-post. Me sender ei melding av og til, og du kan melde deg av når du vil.", "Get menus and news about events by email. We send a message now and then, and you can unsubscribe whenever you like.")}
      </p>
      {state === "done" ? (
        <div className="mt-5 flex items-center gap-3 rounded-full bg-white/[0.08] px-5 py-3.5 text-[0.9375rem] text-white" data-testid="newsletter-success">
          <Check className="h-5 w-5" /> {tx("Du er på lista. Me høyrest!", "You're on the list. Speak soon!")}
        </div>
      ) : (
        <form onSubmit={submit} className="mt-5 grid gap-3 sm:grid-cols-[1fr_1.4fr]">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={tx("Namn", "Name")} className={field} data-testid="newsletter-name-input" aria-label={tx("Namn", "Name")} autoComplete="name" />
          <div className="flex items-center gap-1 rounded-full border border-white/15 bg-white/[0.06] p-1 transition-[border-color] focus-within:border-white/50">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={tx("E-postadresse", "Email address")} className="h-10 min-w-0 flex-1 bg-transparent pl-4 text-[0.9375rem] text-white outline-none placeholder:text-white/40" data-testid="newsletter-email-input" aria-label={tx("E-postadresse", "Email address")} autoComplete="email" />
            <Button type="submit" variant="inverse" className="h-10 shrink-0 px-4" disabled={state === "loading"} data-testid="newsletter-submit">{tx("Meld meg på", "Sign up")}</Button>
          </div>
        </form>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ footer */

const Col = ({ title, children }) => (
  <div>
    <p className="text-sm font-semibold text-white">{title}</p>
    <ul className="mt-4 space-y-2.5 text-[0.9375rem] text-white/60">{children}</ul>
  </div>
);

const FLink = ({ to, children, testid }) => (
  <li><Link to={to} className="inline-flex items-center transition-colors hover:text-white" data-testid={testid}>{children}</Link></li>
);

export const Footer = ({ compact = false }) => {
  const { tx } = useLang();
  return (
    <footer className="relative overflow-hidden bg-primary text-white" data-testid="site-footer">
      {!compact && (
        <Container className="pt-20 lg:pt-28">
          {/* Statement + newsletter */}
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-10">
            <div className="lg:col-span-7">
              <h2 className="max-w-[12ch] text-[2.75rem] leading-[0.96] tracking-[-0.04em] text-white sm:text-[3.5rem] lg:text-[4.5rem]" data-testid="footer-statement">
                {tx("Vi sjåast ved fjorden.", "See you by the fjord.")}
              </h2>
              <p className="mt-6 max-w-md text-lg leading-relaxed text-white/60">
                {tx("Bestill direkte hjå oss. Frukost er inkludert, og du betaler på hotellet.", "Book directly with us. Breakfast is included, and you pay at the hotel.")}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild size="lg" variant="inverse" data-testid="footer-book-button">
                  <Link to="/bestill">{tx("Bestill rom", "Book a room")} <ArrowRight className="h-4 w-4" /></Link>
                </Button>
                <Button asChild size="lg" variant="ghost-light" className="border border-white/20 tabular" data-testid="footer-phone">
                  <a href="tel:+4753457700">+47 53 45 77 00</a>
                </Button>
              </div>
            </div>
            <div className="lg:col-span-5 lg:col-start-8 lg:pt-3">
              <Newsletter />
            </div>
          </div>

          {/* Columns */}
          <div className="mt-16 grid grid-cols-2 gap-x-8 gap-y-12 border-t border-white/10 pt-12 sm:grid-cols-4 lg:mt-20 lg:pt-14">
            <Col title={tx("Utforsk", "Explore")}>
              {NAV.map((n) => <FLink key={n.to} to={n.to} testid={`footer-nav-${n.id}`}>{tx(n.nn, n.en)}</FLink>)}
            </Col>
            <Col title={tx("Bestilling", "Booking")}>
              <FLink to="/bestill" testid="footer-booking-cta">{tx("Bestill rom", "Book a room")}</FLink>
              <FLink to="/mi-bestilling" testid="footer-my-booking">{tx("Mi bestilling", "My booking")}</FLink>
              <FLink to="/arrangement#forespurnad">{tx("Førespurnad om selskap eller møte", "Enquiry for events or meetings")}</FLink>
              <FLink to="/praktisk#vilkar">{tx("Bestillingsvilkår", "Booking terms")}</FLink>
            </Col>
            <Col title={tx("Godt å vite", "Good to know")}>
              <li className="tabular">{tx("Innsjekk frå kl. 15.00", "Check-in from 15:00")}</li>
              <li className="tabular">{tx("Utsjekk før kl. 12.00", "Check-out before 12:00")}</li>
              <li className="tabular">{tx("Frukost kl. 06.00–10.00", "Breakfast 06:00–10:00")}</li>
              <li>{tx("Gratis parkering", "Free parking")}</li>
              <li>{tx("Betaling på hotellet", "Payment at the hotel")}</li>
            </Col>
            <Col title={tx("Kontakt", "Contact")}>
              <li className="text-white/85">Fitjarsjøen 42<br />5419 Fitjar</li>
              <li><a href="tel:+4753457700" className="transition-colors hover:text-white tabular" data-testid="footer-phone-link">+47 53 45 77 00</a></li>
              <li><a href="mailto:post@fitjar-fjordhotell.no" className="break-all transition-colors hover:text-white" data-testid="footer-email">post@fitjar-fjordhotell.no</a></li>
              <li>
                <a href="https://maps.google.com/?q=Fitjarsj%C3%B8en+42,+5419+Fitjar" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 transition-colors hover:text-white" data-testid="footer-directions">
                  {tx("Vegbeskriving", "Directions")} <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              </li>
            </Col>
          </div>
        </Container>
      )}

      <Container className={`${compact ? "py-6" : "mt-14 border-t border-white/10 pt-6 lg:mt-16"}`}>
        <div className="flex flex-col gap-5 text-sm text-white/55 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <Logo className="h-6" light />
            <p className="tabular">© {new Date().getFullYear()} Fitjar Fjordhotell · {tx("Sidan 1986", "Since 1986")}</p>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
            <Link to="/praktisk#vilkar" className="transition-colors hover:text-white">{tx("Personvern", "Privacy")}</Link>
            <Link to="/praktisk#vilkar" className="transition-colors hover:text-white">{tx("Vilkår", "Terms")}</Link>
            <Link to="/admin/login" className="transition-colors hover:text-white" data-testid="footer-admin-link">{tx("For tilsette", "Staff login")}</Link>
            <LangSwitch testid="footer-language-toggle" light />
          </div>
        </div>
      </Container>

      {!compact && (
        <div aria-hidden className="pointer-events-none select-none">
          <Container>
            <p className="-mb-[0.22em] mt-10 overflow-hidden whitespace-nowrap text-[clamp(3.5rem,11.5vw,11rem)] font-bold leading-none tracking-[-0.05em] text-white/[0.06] lg:mt-12">
              Fitjar Fjordhotell
            </p>
          </Container>
        </div>
      )}
      {compact && <div className="h-20 lg:hidden" aria-hidden />}
    </footer>
  );
};
